import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
// import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Mail, CheckCircle, XCircle } from 'lucide-react';
import { apiClient } from '../lib/api';

export function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'idle'>('idle');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyEmail(token);
    } else {
      setStatus('idle');
    }
  }, [searchParams]);

  const verifyEmail = async (token: string) => {
    setStatus('loading');
    try {
      const response = await apiClient.verifyEmail(token);
      setStatus('success');
      setMessage(response.message);
      
      // If token is returned, store it for later use
      if (response.token) {
        apiClient.setToken(response.token);
        // Store in localStorage for session persistence
        localStorage.setItem('go2url_token', response.token);
        if (response.user) {
          localStorage.setItem('go2url_session', JSON.stringify({
            token: response.token,
            user: response.user,
            lastActivity: Date.now()
          }));
        }
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Failed to verify email');
    }
  };

  const resendVerification = async () => {
    if (!email) {
      setMessage('Please enter your email address');
      return;
    }

    setIsResending(true);
    try {
      const response = await apiClient.resendVerificationEmail(email);
      setStatus('success');
      setMessage(response.message);
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
            <p className="text-gray-600">Verifying your email...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === 'success' ? (
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
          ) : status === 'error' ? (
            <XCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          ) : (
            <Mail className="h-12 w-12 text-blue-600 mx-auto mb-4" />
          )}
          <CardTitle>Email Verification</CardTitle>
          <CardDescription>
            {status === 'success' 
              ? 'Your email has been verified successfully!'
              : 'Verify your email to access your account'
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {message && (
            <div className={`p-4 rounded-lg border ${status === 'success' ? 'border-green-600 bg-green-50 text-green-800' : 'border-red-600 bg-red-50 text-red-800'}`}>
              <p>{message}</p>
            </div>
          )}

          {status === 'success' && searchParams.get('token') ? (
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                Redirecting to dashboard...
              </p>
              <Button onClick={() => navigate('/dashboard')} variant="default">
                Go to Dashboard
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <Button 
                onClick={resendVerification} 
                disabled={isResending || !email}
                className="w-full"
              >
                {isResending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Resend Verification Email'
                )}
              </Button>

              <div className="text-center text-sm">
                <span className="text-gray-600">Already verified? </span>
                <button
                  onClick={() => navigate('/login')}
                  className="text-blue-600 hover:underline"
                >
                  Sign in
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default EmailVerification;